import React, { useState, useEffect } from 'react';
import { 
  HiOutlineUser, 
  HiOutlineEnvelope, 
  HiOutlinePhone, 
  HiOutlineIdentification, 
  HiOutlineBriefcase, 
  HiOutlineBuildingOffice2,
  HiOutlinePencil,
  HiCheck,
  HiXMark
} from 'react-icons/hi2';
import { FiInfo } from 'react-icons/fi';
import { authService, masterDataService, pmStandardService } from '../services';
import userService from '../services/userService';
import { toast } from 'react-toastify';
import PageLoading from "../components/common/PageLoading";
import './UserProfile.css';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'settings'
  const [formData, setFormData] = useState({});
  const [pmSettings, setPmSettings] = useState({
    enableEmailNotifications: true,
    notifyOnTaskCreated: '',
    enableTaskCreatedNotif: true,
    notifyOnTaskDue: '',
    enableTaskDueNotif: true,
    notifyOnTaskOverdue: '',
    enableTaskOverdueNotif: true
  });
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  const currentUser = authService.getUser();
  const tenantId = authService.getTenantId();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [userDetails, departmentsData] = await Promise.all([
        userService.getUserById(currentUser.userId),
        masterDataService.getDepartments(tenantId)
      ]);

      setUser(userDetails);
      setFormData({
        fullName: userDetails.fullName || '',
        email: userDetails.email || '',
        phone: userDetails.phone || '',
        designation: userDetails.designation || '',
        departmentId: userDetails.departmentId || ''
      });
      setDepartments(departmentsData);

      if (currentUser.roleName === 'Admin' || currentUser.roleName === 'Super Admin') {
        try {
          const [pmNotifSettings, rolesData] = await Promise.all([
            pmStandardService.getNotificationSettings(),
            masterDataService.getRoles(tenantId)
          ]);
          
          // Merge with defaults to ensure all flags exist
          setPmSettings(prev => ({
            ...prev,
            ...pmNotifSettings
          }));
          setRoles(rolesData || []);
        } catch (e) {
          console.error('Failed to load settings data:', e);
        }
      }
    } catch (err) {
      console.error('Error loading profile data:', err);
      toast.error('Failed to load profile details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePmSettingChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setPmSettings(prev => {
      const updated = { ...prev, [name]: newValue };
      
      // Auto-enable or disable row notification based on recipient selection
      if (name === 'notifyOnTaskCreated') updated.enableTaskCreatedNotif = value !== '';
      if (name === 'notifyOnTaskDue') updated.enableTaskDueNotif = value !== '';
      if (name === 'notifyOnTaskOverdue') updated.enableTaskOverdueNotif = value !== '';
      
      return updated;
    });
  };

  const handleSavePmSetting = async () => {
    setSaving(true);

    try {
      await pmStandardService.saveNotificationSettings(pmSettings);
      toast.success('Settings saved successfully!');
    } catch (err) {
      console.error('Error saving PM settings:', err);
      toast.error('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);

    try {
      const updatedUser = await userService.updateUser(user.userId, {
        ...user,
        ...formData,
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null
      });

      setUser({ ...user, ...formData, ...updatedUser });
      setIsEditing(false);
      toast.success('Profile updated successfully!');
      
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (storedUser.userId === user.userId) {
        localStorage.setItem('user', JSON.stringify({
          ...storedUser,
          fullName: formData.fullName,
          email: formData.email
        }));
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="user-profile-page">
        <PageLoading message="Loading your profile..." />
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="user-profile-container">
        <div className="page-header" style={{ marginBottom: '1.5rem' }}>
          <div>
            <h1>My Profile</h1>
            <p>View and manage your personal information and preferences</p>
          </div>
          <div className="header-actions">
            {activeTab === 'profile' && (
              !isEditing ? (
                <button className="btn-secondary" onClick={handleEditToggle}>
                  <HiOutlinePencil /> Edit Profile
                </button>
              ) : (
                <div className="action-buttons">
                  <button className="btn-secondary" onClick={handleEditToggle} disabled={saving}>
                    <HiXMark /> Cancel
                  </button>
                  <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving...' : <><HiCheck /> Save Changes</>}
                  </button>
                </div>
              )
            )}
          </div>
        </div>

        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-container">
              {user?.fullName?.charAt(0)?.toUpperCase()}
            </div>
            <div className="profile-title">
              <h3>{user?.fullName}</h3>
              <div className="role-badge">{user?.roleName || 'User'}</div>
              <div style={{ marginTop: '0.5rem', opacity: 0.9, fontSize: '0.9rem' }}>
                <HiOutlineIdentification style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                User ID: {user?.userId}
              </div>
            </div>
          </div>

          <div className="profile-tabs">
            <button 
              className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => { setActiveTab('profile'); setIsEditing(false); }}
            >
              Profile Details
            </button>
            <button 
              className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => { setActiveTab('settings'); setIsEditing(false); }}
            >
              Notification Settings
            </button>
          </div>

          <div className="profile-content">
            {activeTab === 'profile' ? (
              <form onSubmit={handleSubmit}>
                <div className="section-title">Personal Information</div>
                <div className="info-grid">
                  <div className="info-item">
                    <label><HiOutlineUser /> Full Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter full name"
                        required
                      />
                    ) : (
                      <div className="value">{user?.fullName || 'N/A'}</div>
                    )}
                  </div>

                  <div className="info-item">
                    <label><HiOutlineEnvelope /> Email Address</label>
                    {isEditing ? (
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="email@example.com"
                      />
                    ) : (
                      <div className="value">{user?.email || 'N/A'}</div>
                    )}
                  </div>

                  <div className="info-item">
                    <label><HiOutlinePhone /> Phone Number</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <div className="value">{user?.phone || 'N/A'}</div>
                    )}
                  </div>

                  <div className="info-item">
                    <label><HiOutlineBriefcase /> Designation</label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="e.g. Plant Engineer"
                      />
                    ) : (
                      <div className="value">{user?.designation || 'N/A'}</div>
                    )}
                  </div>

                  <div className="info-item">
                    <label><HiOutlineBuildingOffice2 /> Department</label>
                    {isEditing ? (
                      <select
                        name="departmentId"
                        value={formData.departmentId}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        <option value="">Select Department</option>
                        {departments.map(d => (
                          <option key={d.departmentId} value={d.departmentId}>{d.departmentName}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="value">
                        {departments.find(d => d.departmentId === user?.departmentId)?.departmentName || 'N/A'}
                      </div>
                    )}
                  </div>

                  <div className="info-item">
                    <label><HiOutlineUser /> Username</label>
                    <div className="value" style={{ opacity: 0.7, background: 'var(--bg-tertiary)' }}>
                      {user?.username}
                    </div>
                  </div>
                </div>
              </form>
            ) : (
              <div className="tab-pane">
                {(currentUser.roleName === 'Admin' || currentUser.roleName === 'Super Admin') && (
                  <div className="settings-section-wrapper">
                    <div className="notification-rows-container">
                      {/* Task Created Row */}
                      <div className="notification-setting-row">
                        <div className="setting-info">
                          <div className="setting-title-group">
                            <FiInfo className="info-icon-blue" title="Notifications sent when a new PM task is generated" />
                            <span className="setting-label">PM Task Creation</span>
                          </div>
                          <p className="setting-description">Triggered when a recurring maintenance task is automatically generated.</p>
                        </div>
                        <div className="setting-controls">
                          <select 
                            className="form-control-clean"
                            name="notifyOnTaskCreated"
                            value={pmSettings.notifyOnTaskCreated || ''}
                            onChange={handlePmSettingChange}
                          >
                            <option value="">Select recipient</option>
                            <option value="assigned_user">Assigned Employee</option>
                            {roles.map(r => (
                              <option key={r.roleId || r.id} value={r.roleName || r.name}>{r.roleName || r.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Task Due Row */}
                      <div className="notification-setting-row">
                        <div className="setting-info">
                          <div className="setting-title-group">
                            <FiInfo className="info-icon-blue" title="Notifications sent when a task is approaching its due date" />
                            <span className="setting-label">PM Task Due Soon</span>
                          </div>
                          <p className="setting-description">Alerts sent when a maintenance task is nearing its scheduled completion date.</p>
                        </div>
                        <div className="setting-controls">
                          <select 
                            className="form-control-clean"
                            name="notifyOnTaskDue"
                            value={pmSettings.notifyOnTaskDue || ''}
                            onChange={handlePmSettingChange}
                          >
                            <option value="">Select recipient</option>
                            <option value="assigned_user">Assigned Employee</option>
                            {roles.map(r => (
                              <option key={r.roleId || r.id} value={r.roleName || r.name}>{r.roleName || r.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Task Overdue Row */}
                      <div className="notification-setting-row">
                        <div className="setting-info">
                          <div className="setting-title-group">
                            <FiInfo className="info-icon-blue" title="Notifications sent when a task exceeds its scheduled date" />
                            <span className="setting-label">PM Task Overdue</span>
                          </div>
                          <p className="setting-description">Escalation alerts sent when a task has not been completed by its due date.</p>
                        </div>
                        <div className="setting-controls">
                          <select 
                            className="form-control-clean"
                            name="notifyOnTaskOverdue"
                            value={pmSettings.notifyOnTaskOverdue || ''}
                            onChange={handlePmSettingChange}
                          >
                            <option value="">Select recipient</option>
                            <option value="assigned_user">Assigned Employee</option>
                            {roles.map(r => (
                              <option key={r.roleId || r.id} value={r.roleName || r.name}>{r.roleName || r.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="settings-form-actions">
                      <button 
                        className="btn-save-settings" 
                        onClick={() => handleSavePmSetting()}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
